import React, { useState, useEffect } from "react";
import { Reveal, Rich, PortraitSlot, Lightbox, fotoFocus } from "./primitives";

/* ---------- Inductee-venster ---------- */
function InducteeModal({ item, portret, fotos, video, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);
  const gold = "var(--gold)";
  const embed = video
    ? (video.bron === "youtube"
      ? `https://www.youtube-nocookie.com/embed/${video.externe_id}?rel=0&modestbranding=1`
      : `https://iframe.videodelivery.net/${video.externe_id}`)
    : null;
  return (
    <div onClick={onClose} data-testid="inductee-modal" style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(10,9,8,0.9)", backdropFilter: "blur(10px)",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: "clamp(16px, 3vw, 48px)", overflowY: "auto", animation: "bioFade .3s var(--ease)",
    }}>
      <article onClick={(e) => e.stopPropagation()} style={{
        background: "var(--ink)", color: "var(--cream)", maxWidth: 1180, width: "100%",
        border: `1px solid ${gold}`, padding: "clamp(28px, 4vw, 64px)",
        position: "relative", animation: "bioRise .4s var(--ease)",
      }}>
        <button onClick={onClose} aria-label="Sluit" data-testid="inductee-modal-close" style={{
          all: "unset", cursor: "pointer", position: "absolute", top: 20, right: 24,
          fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.22em",
          textTransform: "uppercase", color: gold,
        }}>Sluit ×</button>

        <div className="inductee-modal-top" style={{
          display: "grid", gridTemplateColumns: "minmax(0, 340px) minmax(0, 1fr)",
          gap: "clamp(24px, 3vw, 56px)",
        }}>
          <PortraitSlot name={item.naam} ratio="3 / 4" src={portret?.url} alt={portret?.alt} />
          <div>
            <div className="eyebrow" style={{ color: gold, marginBottom: 14 }}>
              {item.award || `Inductee ${item.nummer} · ${item.jaar}`}
            </div>
            <h3 style={{
              fontFamily: "var(--serif)", fontSize: "clamp(34px, 4vw, 62px)",
              lineHeight: 0.98, letterSpacing: "-0.015em", margin: "0 0 22px", fontWeight: 400,
            }}>{item.naam}</h3>
            <Rich as="p" html={item.lede} style={{
              fontFamily: "var(--serif)", fontStyle: "italic",
              fontSize: "clamp(17px, 1.4vw, 22px)", lineHeight: 1.4,
              margin: "0 0 26px", color: gold, maxWidth: "40ch",
            }} />
            <Rich html={item.body} className="swof-body" style={{
              fontFamily: "var(--serif)", fontSize: "clamp(15px, 1.2vw, 18px)",
              lineHeight: 1.65, maxWidth: "58ch", color: "rgba(243,234,215,0.82)", textWrap: "pretty",
            }} />
          </div>
        </div>

        {embed && (
        <div style={{ marginTop: "clamp(32px, 4vw, 56px)" }}>
          <div className="eyebrow" style={{ color: gold, marginBottom: 16 }}>Video</div>
          <div style={{
            position: "relative", aspectRatio: "16 / 9", width: "100%",
            border: "1px solid var(--line)",
            background: "linear-gradient(165deg, #1a150e 0%, rgba(8,7,5,0) 100%), #0b0a08",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <iframe title={video.titel} src={embed} allowFullScreen
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }} />
          </div>
        </div>
        )}

        {fotos && fotos.length > 0 && (
        <div style={{ marginTop: "clamp(32px, 4vw, 56px)" }}>
          <div className="eyebrow" style={{ color: gold, marginBottom: 16 }}>Van de avond</div>
          <div className="inductee-fotos" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 6 }}>
            {fotos.map((f, i) => (
              <PortraitSlot key={i} monogram={String(i + 1)} ratio="3 / 2"
                label={`foto ${i + 1}`} src={f?.url} alt={f?.alt} />
            ))}
          </div>
        </div>
        )}
      </article>
    </div>
  );
}

/* ———— 6. winnaars ———— */
export function BlockWinnaars({ inhoud, inductees, mediaById, videosById, fotosByInductee }) {
  const [open, setOpen] = useState(null);
  const zichtbaar = inductees.filter((i) => i.zichtbaar);
  const gewone = zichtbaar.filter((i) => !i.award);
  const awards = zichtbaar.filter((i) => i.award);
  return (
    <section id="winnaars" data-testid="block-winnaars" style={{
      background: "var(--ink)", color: "var(--cream)",
      padding: "clamp(72px, 9vw, 140px) 0", borderTop: "1px solid var(--line)",
    }}>
      <div className="container">
        <Reveal>
          <div style={{ maxWidth: 820, marginBottom: "clamp(48px, 5vw, 80px)" }}>
            <div className="eyebrow" style={{ color: "var(--gold)", marginBottom: 18 }}>{inhoud.kleine_kop}</div>
            <Rich as="h2" html={inhoud.kop} className="swof-kop" style={{
              fontFamily: "var(--serif)", fontSize: "clamp(40px, 5.4vw, 88px)",
              margin: 0, fontWeight: 400, lineHeight: 1, letterSpacing: "-0.02em",
            }} />
            <Rich html={inhoud.intro} className="swof-body" style={{
              fontFamily: "var(--serif)", fontSize: "clamp(17px, 1.3vw, 21px)",
              lineHeight: 1.6, opacity: 0.8, maxWidth: "58ch", marginTop: 24, textWrap: "pretty",
            }} />
          </div>
        </Reveal>

        <Reveal stagger>
          <div className="winnaars-grid" style={{
            display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
            gap: "clamp(20px, 2.2vw, 40px)",
          }}>
            {gewone.map((w) => (
              <article key={w.id} onClick={() => setOpen(w)} className="inductee-card"
                data-testid={`inductee-card-${w.nummer}`}
                style={{ display: "flex", flexDirection: "column", gap: 14, cursor: "pointer" }}>
                <PortraitSlot name={w.naam} src={mediaById[w.portret_id]?.url}
                  alt={mediaById[w.portret_id]?.alt} varianten={mediaById[w.portret_id]?.varianten} />
                <div style={{ display: "flex", gap: 12, alignItems: "baseline" }}>
                  <span style={{
                    fontFamily: "var(--serif)", fontStyle: "italic", fontSize: 20,
                    color: "var(--gold-deep)", lineHeight: 1,
                  }}>{w.nummer}</span>
                  <h3 style={{
                    fontFamily: "var(--serif)", fontSize: "clamp(19px, 1.5vw, 25px)",
                    margin: 0, fontWeight: 400, lineHeight: 1.15, letterSpacing: "-0.01em",
                  }}>{w.naam}</h3>
                </div>
                <Rich html={w.korte_tekst} className="swof-body" style={{
                  fontFamily: "var(--sans)", fontSize: 14, lineHeight: 1.75,
                  opacity: 0.68, margin: 0, textWrap: "pretty",
                }} />
                <span className="inductee-more" style={{
                  fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.2em",
                  textTransform: "uppercase", color: "var(--gold)", opacity: 0.7,
                }}>Meer →</span>
              </article>
            ))}
          </div>
        </Reveal>

        {awards.length > 0 && (
          <>
            <Reveal>
              <div style={{ maxWidth: 820, margin: "clamp(64px, 7vw, 112px) 0 clamp(32px, 3.5vw, 52px)" }}>
                <div className="eyebrow" style={{ color: "var(--gold)", marginBottom: 18 }}>{inhoud.award_kleine_kop}</div>
                <Rich as="h2" html={inhoud.award_kop} className="swof-kop" style={{
                  fontFamily: "var(--serif)", fontSize: "clamp(36px, 4.6vw, 76px)",
                  margin: 0, fontWeight: 400, lineHeight: 1, letterSpacing: "-0.02em",
                }} />
                <Rich html={inhoud.award_intro} className="swof-body" style={{
                  fontFamily: "var(--serif)", fontSize: "clamp(17px, 1.3vw, 21px)",
                  lineHeight: 1.6, opacity: 0.8, maxWidth: "58ch",
                  marginTop: 24, marginBottom: 0, textWrap: "pretty",
                }} />
                <Rich as="p" html={inhoud.award_regel} style={{
                  fontFamily: "var(--serif)", fontStyle: "italic",
                  fontSize: "clamp(17px, 1.25vw, 20px)", lineHeight: 1.6,
                  color: "var(--gold)", maxWidth: "54ch", margin: "22px 0 0", paddingLeft: 22,
                  borderLeft: "1px solid rgba(201,169,97,0.45)", textWrap: "pretty",
                }} />
              </div>
            </Reveal>
            {awards.map((a) => (
              <Reveal key={a.id}>
                <div className="starlight-card inductee-card" data-testid="starlight-card"
                  onClick={() => setOpen(a)} style={{
                    display: "grid", gridTemplateColumns: "minmax(0, 280px) minmax(0, 1fr)",
                    gap: "clamp(28px, 4vw, 64px)", alignItems: "center",
                    border: "1px solid rgba(201,169,97,0.34)",
                    background: "linear-gradient(120deg, rgba(201,169,97,0.06) 0%, rgba(201,169,97,0) 60%)",
                    padding: "clamp(24px, 3vw, 44px)", cursor: "pointer",
                  }}>
                  <PortraitSlot name={a.naam} ratio="1 / 1" src={mediaById[a.portret_id]?.url} alt={mediaById[a.portret_id]?.alt} />
                  <div>
                    <div className="eyebrow" style={{ color: "var(--gold)", marginBottom: 14 }}>{a.award}</div>
                    <h3 style={{
                      fontFamily: "var(--serif)", fontSize: "clamp(34px, 3.6vw, 60px)",
                      margin: 0, fontWeight: 400, lineHeight: 1.05, letterSpacing: "-0.01em",
                    }}>{a.naam}</h3>
                    <Rich as="p" html={a.korte_tekst} style={{
                      fontFamily: "var(--serif)", fontSize: "clamp(16px, 1.15vw, 19px)",
                      lineHeight: 1.65, opacity: 0.82, maxWidth: "54ch",
                      marginTop: 16, marginBottom: 0, textWrap: "pretty",
                    }} />
                    <span className="inductee-more" style={{
                      display: "inline-block", marginTop: 18, fontFamily: "var(--mono)",
                      fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
                      color: "var(--gold)", opacity: 0.7,
                    }}>Meer →</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </>
        )}
      </div>
      {open && (
        <InducteeModal item={open} onClose={() => setOpen(null)}
          portret={mediaById[open.portret_id]}
          video={inhoud.toon_video ? videosById[open.video_id] : null}
          fotos={inhoud.toon_fotos ? (fotosByInductee[open.id] || []).map((f) => mediaById[f.media_id]).filter(Boolean) : []} />
      )}
    </section>
  );
}

/* ———— 7. galerij ———— */
export function BlockGalerij({ inhoud, fotos, mediaById, fotogalerieAan }) {
  const [open, setOpen] = useState(null);
  const uitgelicht = fotos.filter((f) => f.in_uitgelicht);
  const tiles = uitgelicht.length
    ? uitgelicht
    : Array.from({ length: inhoud.aantal_tegels || 15 }, () => null);
  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <section id="galerij" data-testid="block-galerij" style={{
      background: "#050403", color: "var(--cream)",
      padding: "clamp(72px, 9vw, 140px) 0", borderTop: "1px solid var(--line)",
    }}>
      <div className="container">
        <Reveal>
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 24, alignItems: "baseline",
            justifyContent: "space-between", marginBottom: "clamp(40px, 4.5vw, 68px)",
          }}>
            <div>
              <div className="eyebrow" style={{ color: "var(--gold)", marginBottom: 16 }}>{inhoud.kleine_kop}</div>
              <Rich as="h2" html={inhoud.kop} className="swof-kop" style={{
                fontFamily: "var(--serif)", fontSize: "clamp(38px, 5vw, 82px)",
                margin: 0, fontWeight: 400, lineHeight: 1, letterSpacing: "-0.02em",
              }} />
            </div>
            <p style={{
              fontFamily: "var(--sans)", fontSize: 13, letterSpacing: "0.06em",
              opacity: 0.5, margin: 0, maxWidth: "32ch",
            }}>{inhoud.hulptekst}</p>
          </div>
        </Reveal>

        <Reveal stagger>
          <div className="galerij-grid" style={{
            display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 6,
          }}>
            {tiles.map((t, i) => (
              <button key={i} onClick={() => setOpen(i)} aria-label={`Foto ${i + 1}`} style={{
                padding: 0, border: "none", background: "transparent", cursor: "pointer", display: "block",
              }}>
                <PortraitSlot monogram={String(i + 1)} ratio="3 / 2" label={`foto ${i + 1}`}
                  src={t ? mediaById[t.media_id]?.url : null} alt={t ? mediaById[t.media_id]?.alt : null}
                  varianten={t ? mediaById[t.media_id]?.varianten : null}
                  focus={t ? fotoFocus(mediaById[t.media_id]) : undefined} />
              </button>
            ))}
          </div>
        </Reveal>

        {fotogalerieAan && (
          <Reveal>
            <div className="galerij-alle" style={{ marginTop: "clamp(24px, 3vw, 40px)" }}>
              <button data-testid="galerij-alle-knop"
                onClick={() => window.dispatchEvent(new CustomEvent("swof:open-galerie"))}
                style={{
                  fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.2em",
                  textTransform: "uppercase", color: "var(--gold)", background: "transparent",
                  border: "1px solid rgba(201,169,97,0.4)", padding: "14px 26px", cursor: "pointer",
                  transition: "background .3s var(--ease), border-color .3s var(--ease)",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(201,169,97,0.08)"; e.currentTarget.style.borderColor = "rgba(201,169,97,0.75)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(201,169,97,0.4)"; }}
              >{inhoud.knop_tekst}</button>
            </div>
          </Reveal>
        )}
        {inhoud.credit && (
          <p data-testid="galerij-credit" style={{
            marginTop: 28, textAlign: "right", fontFamily: "var(--mono)", fontSize: 9,
            letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(243,234,215,0.4)",
          }}>{inhoud.credit}</p>
        )}
      </div>

      {open !== null && tiles[open] && (
        <Lightbox items={tiles.map((t) => mediaById[t?.media_id]).filter(Boolean)} index={open}
          onClose={() => setOpen(null)} onChange={setOpen} testId="galerij-lightbox" />
      )}
    </section>
  );
}

/* ———— 8. verslag ———— */
export function BlockVerslag({ inhoud }) {
  return (
    <section id="verslag" data-testid="block-verslag" className="tone-cream" style={{
      background: "var(--cream)", color: "var(--cream-ink)", padding: "clamp(72px, 9vw, 140px) 0",
    }}>
      <div className="container">
        <Reveal>
          <div style={{ display: "flex", alignItems: "baseline", gap: 32, marginBottom: 48, flexWrap: "wrap" }}>
            <div className="eyebrow" style={{ color: "var(--gold-deep)" }}>{inhoud.kleine_kop}</div>
            <div style={{ flex: 1, height: 1, background: "var(--line-cream)", minWidth: 40 }} />
          </div>
          <Rich as="h2" html={inhoud.kop} className="display swof-kop" style={{
            fontSize: "clamp(52px, 9vw, 168px)", lineHeight: 0.94, letterSpacing: "-0.035em",
            margin: "0 0 clamp(40px, 5vw, 72px)", fontWeight: 400,
          }} />
        </Reveal>
        <Reveal>
          <Rich as="p" html={inhoud.lede} style={{
            maxWidth: "32ch", margin: "0 0 clamp(56px, 7vw, 96px)",
            fontFamily: "var(--serif)", fontStyle: "italic",
            fontSize: "clamp(28px, 3vw, 44px)", lineHeight: 1.18, letterSpacing: "-0.015em",
          }} />
        </Reveal>
        <Reveal>
          <Rich html={inhoud.body} className="verslag-body swof-body swof-body--lead" style={{
            columnCount: 2, columnGap: "clamp(40px, 5vw, 80px)", fontFamily: "var(--serif)",
            fontSize: "clamp(18px, 1.45vw, 22px)", lineHeight: 1.6, textWrap: "pretty",
          }} />
        </Reveal>
        <Reveal>
          <Rich as="p" html={inhoud.slotcitaat} style={{
            marginTop: "clamp(56px, 7vw, 104px)", paddingTop: 32,
            borderTop: "1px solid var(--line-cream)", fontFamily: "var(--serif)",
            fontStyle: "italic", fontSize: "clamp(24px, 2.6vw, 40px)", lineHeight: 1.25,
            color: "var(--gold-deep)", maxWidth: "34ch", marginBottom: 0,
          }} />
        </Reveal>
      </div>
    </section>
  );
}

/* ———— 9. aftermovie ———— */
export function BlockAftermovie({ inhoud, video }) {
  const [playing, setPlaying] = useState(false);
  const embed = video
    ? (video.bron === "youtube"
      ? `https://www.youtube-nocookie.com/embed/${video.externe_id}?autoplay=1&rel=0&modestbranding=1`
      : `https://iframe.videodelivery.net/${video.externe_id}?autoplay=true`)
    : null;
  return (
    <section id="aftermovie" data-testid="block-aftermovie" style={{
      background: "var(--ink)", color: "var(--cream)",
      padding: "clamp(72px, 9vw, 140px) 0", borderTop: "1px solid var(--line)",
    }}>
      <div className="container">
        <Reveal>
          <div style={{ textAlign: "center", marginBottom: "clamp(32px, 4vw, 56px)" }}>
            <div className="eyebrow" style={{ color: "var(--gold)", marginBottom: 16 }}>{inhoud.kleine_kop}</div>
            <Rich as="h2" html={inhoud.kop} className="swof-kop" style={{
              fontFamily: "var(--serif)", fontSize: "clamp(34px, 4.4vw, 72px)",
              margin: 0, fontWeight: 400, lineHeight: 1.02, letterSpacing: "-0.02em",
            }} />
          </div>
        </Reveal>
        <Reveal>
          <div style={{
            position: "relative", aspectRatio: "16 / 9", width: "100%",
            border: "1px solid var(--line)",
            background: "linear-gradient(165deg, #1a150e 0%, #08070500 100%), #0b0a08",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {playing && embed ? (
              <iframe title={video.titel} src={embed} allowFullScreen
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }} />
            ) : (
              <>
                <button onClick={() => embed && setPlaying(true)} aria-label="Speel aftermovie af"
                  disabled={!embed} style={{
                    width: "clamp(64px, 7vw, 92px)", aspectRatio: "1", borderRadius: "50%",
                    border: "1px solid rgba(201,169,97,0.6)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "var(--gold)", fontSize: 20, paddingLeft: 4,
                    cursor: embed ? "pointer" : "default", background: "transparent",
                  }}>▶</button>
                {!embed && (
                  <span style={{
                    position: "absolute", bottom: 16, left: 18,
                    fontFamily: "var(--mono)", fontSize: 9, letterSpacing: "0.2em",
                    textTransform: "uppercase", color: "rgba(243,234,215,0.34)",
                  }}>video volgt</span>
                )}
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
